import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { AuthShell, Field } from "@/components/observer/AuthShell";
import { btnPrimary } from "@/lib/ui";
import { useAuth } from "@/lib/user";

// Opened from a setup link an admin sent: a new account's first password, or a reset.
export default function Welcome() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/setup/${encodeURIComponent(token)}`).then(({ data }) => setInfo(data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setError("The two passwords don't match");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/auth/setup/${encodeURIComponent(token)}`, { password: pw.a });
      signIn(data.user);
      nav("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  const reset = info?.purpose === "reset";
  return (
    <AuthShell
      testid="welcome-page"
      eyebrow={reset ? "Password reset" : "Welcome"}
      title={info ? (reset ? <>Choose a new<br />password.</> : <>Hi {info.name.split(" ")[0]}.<br />Set your password.</>) : <>Setup link</>}
      intro={info ? `For ${info.email}. At least 10 characters; a short phrase is easiest to remember.` : null}
      footer="This link works once and expires after 72 hours."
    >
      {!info && !error && <div className="h-24 shimmer" />}
      {!info && error && <p className="text-sm text-[#EF4444]" data-testid="welcome-error">{error}</p>}
      {info && (
        <form onSubmit={submit} className="space-y-4" data-testid="welcome-form">
          <input type="email" value={info.email} autoComplete="username" readOnly hidden />
          <Field label="New password" type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} autoComplete="new-password" autoFocus data-testid="welcome-password" />
          <Field label="Same again" type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} autoComplete="new-password" data-testid="welcome-password-again" />
          {error && <p className="text-sm text-[#EF4444] font-mono" data-testid="welcome-error">{error}</p>}
          <button type="submit" disabled={busy || !pw.a || !pw.b} className={`${btnPrimary} w-full py-3.5`} data-testid="welcome-submit">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : null} Save and sign in <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      )}
    </AuthShell>
  );
}
